<template>
    <h3
        v-if="!externallyControlled"
        class="flex mb-2 text-lg font-medium font-bold text-gray-400 align-bottom align-center"
    >
        <span> Comissões </span>

        <select
            v-model="localInterval"
            :disabled="loading"
            class="ml-auto rounded-lg border border-gray-300 bg-gray-50 p-2.5 text-sm text-gray-900 focus:border-blue-500 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-500 dark:focus:ring-blue-500"
        >
            <option :value="7">7 dias</option>
            <option :value="30">30 dias</option>
            <option :value="60">60 dias</option>
            <option :value="0">Todo o Período</option>
        </select>
    </h3>
    <div class="grid grid-cols-3 gap-3 mb-4">
        <card :loading="loading" :size="size">
            <p
                class="flex mb-2 text-sm font-medium text-gray-600 align-center dark:text-gray-400"
            >
                Pagas
                <span class="ml-auto">
                    {{ cards.paid.count }}
                </span>
            </p>
            <p
                class="flex text-lg font-semibold text-white-100 dark:text-gray-200"
            >
                {{ formatCurrency(cards.paid.amount) }}
            </p>
        </card>
        <card :loading="loading" :size="size">
            <p
                class="flex mb-2 text-sm font-medium text-gray-600 align-center dark:text-gray-400"
            >
                Reembolsadas
                <span class="ml-auto">
                    {{ cards.refunded.count }} {{ refundPercentage }}%
                </span>
            </p>
            <p class="text-lg font-semibold text-white-100 dark:text-gray-200">
                {{ formatCurrency(cards.refunded.amount) }}
            </p>
        </card>
        <card :loading="loading" :size="size">
            <p
                class="flex mb-2 text-sm font-medium text-gray-600 align-center dark:text-gray-400"
            >
                Disputa + ChargeBack
                <span class="ml-auto font-semibold" :class="rateFlagClass(cards.new_chargeback_rates.chargeback_rate)">
                    {{ parseInt(cards.disputed.count) + parseInt(cards.chargeback.count) }} {{ cards.new_chargeback_rates.chargeback_rate }}%
                </span>
            </p>
            <p class="text-lg font-semibold text-white-100 dark:text-gray-200">
                {{ formatCurrency(Number(cards.disputed.amount) + Number(cards.chargeback.amount)) }}
            </p>
        </card>
    </div>

    <!-- Nova seção para taxas de chargeback detalhadas -->
    <div v-if="hasChargebackRates" class="grid grid-cols-3 gap-3 mb-4">
        <card :loading="loading" :size="size">
            <p
                class="flex mb-2 text-sm font-medium text-gray-600 align-center dark:text-gray-400"
            >
                Taxa de Chargeback Total
                <span class="ml-auto font-semibold" :class="rateFlagClass(cards.chargeback_rates.total)">
                    {{ cards.chargeback_rates.total }}%
                </span>
            </p>
            <div class="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                <div 
                    class="bg-red-600 h-2.5 rounded-full" 
                    :style="`width: ${Math.min(100, cards.chargeback_rates.total)}%`"
                ></div>
            </div>
        </card>
        <card :loading="loading" :size="size">
            <p
                class="flex mb-2 text-sm font-medium text-gray-600 align-center dark:text-gray-400"
            >
                Taxa com Afiliados
                <span class="ml-auto font-semibold" :class="rateFlagClass(cards.chargeback_rates.with_affiliates)">
                    {{ cards.chargeback_rates.with_affiliates }}%
                </span>
            </p>
            <div class="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                <div 
                    class="bg-orange-600 h-2.5 rounded-full" 
                    :style="`width: ${Math.min(100, cards.chargeback_rates.with_affiliates)}%`"
                ></div>
            </div>
        </card>
        <card :loading="loading" :size="size">
            <p
                class="flex mb-2 text-sm font-medium text-gray-600 align-center dark:text-gray-400"
            >
                Taxa sem Afiliados
                <span class="ml-auto font-semibold" :class="rateFlagClass(cards.chargeback_rates.without_affiliates)">
                    {{ cards.chargeback_rates.without_affiliates }}%
                </span>
            </p>
            <div class="w-full bg-gray-200 rounded-full h-2.5 dark:bg-gray-700">
                <div 
                    class="bg-yellow-600 h-2.5 rounded-full" 
                    :style="`width: ${Math.min(100, cards.chargeback_rates.without_affiliates)}%`"
                ></div>
            </div>
        </card>
    </div>
</template>

<script>
import Card from "../layouts/Card.vue";
export default {
    components: { Card },
    props: {
        user: { required: true },
        size: { type: String, default: "md" },
        // Quando informado, o período passa a ser controlado pelo componente pai
        // (ex.: seletor global de período) e o dropdown local fica oculto.
        period: { type: Number, default: null },
    },
    data() {
        return {
            loading: true,
            localInterval: 30,
            cards: {
                paid: {
                    total: 0,
                    count: 0,
                },
                refunded: {
                    total: 0,
                    count: 0,
                },
                disputed: {
                    total: 0,
                    count: 0,
                },
                chargeback: {
                    total: 0,
                    count: 0,
                },
                chargeback_rates: {
                    total: 0,
                    with_affiliates: 0,
                    without_affiliates: 0,
                }
            },
        };
    },

    computed: {
        externallyControlled() {
            return this.period !== null;
        },
        activeInterval() {
            return this.externallyControlled ? this.period : this.localInterval;
        },
        hasChargebackRates() {
            return this.cards && 
                this.cards.chargeback_rates && 
                (this.cards.chargeback_rates.total > 0 || 
                 this.cards.chargeback_rates.with_affiliates > 0 || 
                 this.cards.chargeback_rates.without_affiliates > 0);
        },
        chargebackPercentage() {
            if (this.cards.chargeback.count === 0) {
                return 0;
            }

            return (
                (this.cards.chargeback.count / this.cards.paid.count) *
                100
            ).toFixed(2);
        },
        refundPercentage() {
            if (this.cards.refunded.count === 0) {
                return 0;
            }

            return (
                (this.cards.refunded.count / this.cards.paid.count) *
                100
            ).toFixed(2);
        },
    },

    watch: {
        activeInterval() {
            this.fetch();
        },
        user() {
            this.fetch();
        },
    },

    mounted() {
        if (this.user) {
            this.fetch();
        }
    },

    methods: {
        rateFlagClass(rate) {
            const value = Number(rate) || 0;

            if (value >= 1.5) {
                return "text-red-600 dark:text-red-400";
            }

            if (value > 1) {
                return "text-amber-600 dark:text-amber-400";
            }

            return "";
        },
        fetch() {
            this.loading = true;
            axios
                .get("/api/user/" + this.user.id + "/commission/summaryCards", {
                    params: {
                        interval: this.activeInterval,
                    },
                })
                .then(({ data }) => {
                    this.cards = data;
                })
                .catch((error) => {
                    console.error("Erro ao buscar comissões:", error);
                })
                .finally(() => {
                    this.loading = false;
                });
        },
        formatCurrency(value) {
            return (value / 100).toLocaleString("pt-br", {
                style: "currency",
                currency: "BRL",
            });
        },
    },
};
</script>
